import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Form,
  Spinner,
} from "react-bootstrap";
import { ArrowLeft, CheckCircle, Gift } from "lucide-react";

import Navbar from "../components/NavbarM";

import {
  getMemberAtStore,
  listMemberStoreBalances,
  redeemPoints,
} from "../services/memberService";

import {
  calculateRedemptionValueCents,
  getRedemptionPayoutPercent,
  loadRewardSettings,
} from "../services/rewardSettingsService";

function RedeemPointsPage() {
  const navigate = useNavigate();
  const { memberId } = useParams();

  const storeId = localStorage.getItem("storeId");

  const [member, setMember] = useState(null);
  const [balances, setBalances] = useState([]);
  const [rewardSettings, setRewardSettings] = useState(null);

  const [sourceStoreId, setSourceStoreId] = useState("");
  const [pointsToRedeem, setPointsToRedeem] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [successResult, setSuccessResult] = useState(null);

  useEffect(() => {
    async function loadPage() {
      setLoading(true);
      setError("");

      try {
        const [customerResult, balancesResult, settingsResult] =
          await Promise.all([
            getMemberAtStore(memberId),
            listMemberStoreBalances(memberId),
            loadRewardSettings(),
          ]);

        setMember(customerResult.member);
        setRewardSettings(settingsResult);

        setBalances(balancesResult);

        const defaultStore =
          balancesResult.find((balance) => balance.storeId === storeId) ||
          balancesResult[0];

        setSourceStoreId(defaultStore?.storeId || "");
      } catch (loadError) {
        console.error(loadError);

        setError(
          loadError.message ||
            "The customer information could not be loaded."
        );
      } finally {
        setLoading(false);
      }
    }

    loadPage();
  }, [memberId, storeId]);

  const selectedBalance = useMemo(
    () => balances.find((balance) => balance.storeId === sourceStoreId),
    [balances, sourceStoreId]
  );

  const isCrossStore = sourceStoreId !== "" && sourceStoreId !== storeId;

  const payoutPercent = useMemo(() => {
    if (!rewardSettings) return 100;

    try {
      return getRedemptionPayoutPercent(rewardSettings, isCrossStore);
    } catch {
      return 100;
    }
  }, [rewardSettings, isCrossStore]);

  const previewPayoutDollars = useMemo(() => {
    const points = Number(pointsToRedeem);

    if (!rewardSettings || !points || points <= 0) return 0;

    try {
      const cents = calculateRedemptionValueCents({
        points,
        redemptionPointsPerDollar: rewardSettings.redemptionPointsPerDollar,
        isCrossStore,
        crossStorePercent: rewardSettings.crossStoreRedemptionPercent,
      });

      return cents / 100;
    } catch {
      return 0;
    }
  }, [pointsToRedeem, rewardSettings, isCrossStore]);

  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setSuccessResult(null);
    setSaving(true);

    try {
      const result = await redeemPoints({
        memberId,
        sourceStoreId,
        pointsToRedeem,
      });

      setSuccessResult(result);

      setBalances((currentBalances) =>
        currentBalances
          .map((balance) =>
            balance.storeId === sourceStoreId
              ? { ...balance, points: result.pointsAfter }
              : balance
          )
          .filter((balance) => balance.points > 0)
      );

      setPointsToRedeem("");

      setTimeout(() => {
        navigate("/Home", { replace: true });
      }, 1800);
    } catch (saveError) {
      console.error(saveError);

      setError(
        saveError.message || "The points could not be redeemed."
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <>
        <Navbar />

        <Container className="py-5 text-center">
          <Spinner animation="border" />
          <p className="text-muted mt-3">Loading customer account</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <Container className="py-4" style={{ maxWidth: "850px" }}>
        <Button
          variant="link"
          className="px-0 mb-3 text-decoration-none"
          onClick={() => navigate("/pages/scan-customer")}
        >
          <ArrowLeft size={18} className="me-2" />
          Back to Scan
        </Button>

        {error && <Alert variant="danger">{error}</Alert>}

        {successResult && (
          <Alert
            variant="success"
            className="d-flex align-items-start gap-3"
          >
            <CheckCircle size={24} className="flex-shrink-0 mt-1" />

            <div>
              <strong>Points redeemed successfully</strong>

              <div className="mt-1">
                {successResult.pointsRedeemed.toLocaleString()} points were
                redeemed for $
                {successResult.redemptionValueDollars.toFixed(2)}.
              </div>

              <div>
                Remaining balance at that store:{" "}
                {successResult.pointsAfter.toLocaleString()} points
              </div>
            </div>
          </Alert>
        )}

        {member && (
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <h2 className="mb-2">{member.name}</h2>

              <p className="text-muted mb-0">
                Phone ending in {member.phoneLastFour || "N/A"}
              </p>
            </Card.Body>
          </Card>
        )}

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4 p-md-5">
            <div className="d-flex align-items-center gap-3 mb-4">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: 52,
                  height: 52,
                  background: "#fff3e8",
                  color: "#f97316",
                }}
              >
                <Gift size={26} />
              </div>

              <div>
                <h3 className="mb-1">Redeem Points</h3>
                <p className="text-muted mb-0">
                  Choose which store's balance to redeem from.
                </p>
              </div>
            </div>

            {balances.length === 0 ? (
              <Alert variant="light" className="border mb-0">
                This customer has no redeemable points at any store.
              </Alert>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Redeem from</Form.Label>

                  <Form.Select
                    size="lg"
                    value={sourceStoreId}
                    onChange={(event) =>
                      setSourceStoreId(event.target.value)
                    }
                    disabled={saving}
                  >
                    {balances.map((balance) => (
                      <option key={balance.storeId} value={balance.storeId}>
                        {balance.storeName || balance.storeId} —{" "}
                        {balance.points.toLocaleString()} pts
                        {balance.storeId === storeId ? " (this store)" : ""}
                      </option>
                    ))}
                  </Form.Select>

                  {isCrossStore && (
                    <Form.Text>
                      Cross-store redemption pays {payoutPercent}% of full
                      value.
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Points to redeem</Form.Label>

                  <Form.Control
                    type="number"
                    size="lg"
                    min="1"
                    step="1"
                    max={selectedBalance?.points || undefined}
                    value={pointsToRedeem}
                    onChange={(event) =>
                      setPointsToRedeem(event.target.value)
                    }
                    placeholder="0"
                    required
                    disabled={saving}
                  />

                  <Form.Text>
                    Available: {(selectedBalance?.points || 0).toLocaleString()}{" "}
                    points
                  </Form.Text>
                </Form.Group>

                <Card className="bg-light border-0 mb-4">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="text-muted small">Payout value</div>

                        <strong className="fs-4">
                          ${previewPayoutDollars.toFixed(2)}
                        </strong>
                      </div>

                      {isCrossStore && (
                        <Badge bg="warning" text="dark">
                          Cross-store
                        </Badge>
                      )}
                    </div>
                  </Card.Body>
                </Card>

                <Button
                  type="submit"
                  size="lg"
                  className="w-100"
                  disabled={saving || previewPayoutDollars <= 0}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Redeeming
                    </>
                  ) : (
                    <>
                      <Gift size={19} className="me-2" />
                      Confirm Redemption
                    </>
                  )}
                </Button>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default RedeemPointsPage;
